import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import { DEPLOY_PRODUCTION } from '@/const'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import { Token, type AccountToken, type Maybe } from '@/types'
import type { ICoinInfo, ITokenInfo } from '@/types/api'
import { arrayPushUnique, getExecuteOnce, OffsetSettingInfoList } from '@/utils'

export type TypeTokenList = {
  [key: string]: ITokenInfo
}
const UNIQUEKEY = 'id'
const useCoinAPIStore = defineStore('coinAPI', () => {
  const filterCoinList = ref<Maybe<ICoinInfo[]>>([])
  const coinList = ref<Maybe<ICoinInfo[]>>([])
  // V2: currencyId는 문자열, V1 호환을 위해 number도 허용
  const tokenListByCoinId = ref<{ [key: string | number]: ITokenInfo[] }>({})
  const coinOffsetSettingInfoList = new OffsetSettingInfoList<
    typeof fetchCoinList
  >()
  const tokenOffsetSettingInfoList = new OffsetSettingInfoList<
    typeof fetchTokenList
  >()

  const fetchCoinList = getExecuteOnce(
    async ({
      coinIds,
      force = false,
      needToAddData,
      recursionsParamsData,
      reset = false,
      locale,
    }: {
      coinIds?: number[]
      force?: boolean
      needToAddData?: boolean
      recursionsParamsData?: number
      reset?: boolean
      locale?: number
    }) => {
      let ids: string | string[] = ''
      const offsetSettingInfo = {
        offset: recursionsParamsData ?? 1,
      }

      if (coinIds && coinIds.length > 0) {
        ids = coinIds
          .filter((id, index) => coinIds.indexOf(id) === index)
          .map((id) => id.toString())
      }

      if (reset) {
        coinOffsetSettingInfoList.removeOffset({
          coinIds,
          force,
          needToAddData,
        })
        reset = false
      }

      if (needToAddData) {
        offsetSettingInfo.offset = coinOffsetSettingInfoList.offsetSettingInfo({
          params: { coinIds, force, needToAddData },
          offset: offsetSettingInfo.offset,
        }) as number
      }
      if (force || coinList.value?.length === 0) {
        const result = (
          await WepinBackendAPI.getInstance().coin.getCoinInfoList({
            ids,
            page: offsetSettingInfo.offset,
            locale: locale,
          })
        ).coins

        coinList.value = arrayPushUnique(
          result,
          UNIQUEKEY,
          coinList.value as ICoinInfo[],
        )
        /** PRODUCTION 상태시 TEST NET && !forcedDisplay 라면 제거*/
        if (import.meta.env.VITE_DEPLOY_ENV === DEPLOY_PRODUCTION) {
          filterCoinList.value = arrayPushUnique(
            coinList.value?.filter(
              (coin) => coin.isTestnet && !coin.property?.forcedDisplay,
            ) as ICoinInfo[],
            UNIQUEKEY,
            filterCoinList.value as ICoinInfo[],
          )

          coinList.value = coinList.value?.filter((coin) => {
            return !coin.isTestnet || coin.property?.forcedDisplay
          })
        }
        if (needToAddData && offsetSettingInfo.offset !== 0) {
          coinOffsetSettingInfoList.checkLimit(
            {
              params: { coinIds, force, needToAddData },
              offset: offsetSettingInfo.offset,
            },
            result.length,
          ) as number
        }
      }
      if (needToAddData && offsetSettingInfo.offset !== 0) {
        await fetchCoinList({
          coinIds,
          force,
          needToAddData,
          recursionsParamsData: offsetSettingInfo.offset,
          reset: reset,
          locale: locale,
        })
      }
      return coinList.value
    },
  )

  const tokenList = computed<ITokenInfo[]>(() => {
    if (!tokenListByCoinId.value) return []
    return Object.values(tokenListByCoinId.value).flat()
  })

  const getCoinInfo = (coinId: number): Maybe<ICoinInfo> => {
    return coinList.value?.find(({ id }) => id === coinId)
  }

  const fetchHeldCoinList = getExecuteOnce(
    async ({
      accountToken,
      force = false,
      locale,
    }: {
      accountToken: AccountToken
      force?: boolean
      locale?: number
    }) => {
      // V2: currency 정보가 이미 있으면 API 호출 불필요
      const accountTokenData = accountToken as any
      if (accountTokenData.currency) {
        return []
      }

      // V2: currencyId 또는 V1: tokenId로 API 조회
      const currencyId = accountToken.currencyId
      const tokenId = accountToken.tokenId

      if (!currencyId && !tokenId) {
        return []
      }

      // V2: currencyId를 키로 사용, V1 호환: coinId
      const cacheKey =
        currencyId ||
        accountToken.account?.currencyId ||
        tokenId?.toString() ||
        ''
      if (force || !tokenListByCoinId.value[cacheKey]) {
        const response =
          await WepinBackendAPI.getInstance().coin.getTokenInfoList({
            ids: tokenId?.toString() || currencyId || '',
            locale: locale,
          })
        if (!response) return
        tokenListByCoinId.value[cacheKey] = arrayPushUnique(
          response.tokens,
          UNIQUEKEY,
          tokenListByCoinId.value[cacheKey] as ITokenInfo[],
        )
      }
      return tokenListByCoinId.value[cacheKey]
    },
  )

  const searchTokenList = async ({
    searchValue,
    page,
    gotAllTokens,
    locale,
  }: {
    searchValue: string
    page: number
    gotAllTokens: Ref<boolean>
    locale?: number
  }) => {
    const response = (
      await WepinBackendAPI.getInstance().coin.getTokenInfoList({
        name: searchValue,
        symbol: searchValue,
        contract: searchValue,
        page: page,
        locale: locale,
      })
    ).tokens

    response.forEach((token: ITokenInfo) => {
      if (!tokenListByCoinId.value[token.coinId])
        tokenListByCoinId.value[token.coinId] = []

      if (
        tokenListByCoinId.value[token.coinId].findIndex(
          (tokenByCoinId: ITokenInfo) => {
            return tokenByCoinId.id === token.id
          },
        ) === -1
      ) {
        tokenListByCoinId.value[token.coinId].push(token)
      }
    })
    if (response.length < 15) gotAllTokens.value = true
    return response.map((token: ITokenInfo) => new Token(token))
  }

  const fetchTokenList = getExecuteOnce(
    async ({
      needToAddData,
      force = false,
      reset = false,
      locale,
    }: {
      needToAddData: boolean
      force?: boolean
      reset?: boolean
      locale?: number
    }) => {
      const offsetSettingInfo = {
        offset: 0,
      }
      if (force) {
        if (reset) {
          tokenOffsetSettingInfoList.removeOffset({
            force,
            needToAddData,
          })
        }

        offsetSettingInfo.offset = tokenOffsetSettingInfoList.offsetSettingInfo(
          {
            params: { needToAddData, force },
            offset: offsetSettingInfo.offset,
          },
        ) as number

        const response =
          await WepinBackendAPI.getInstance().coin.getTokenInfoList({
            page: offsetSettingInfo.offset,
            locale: locale,
          })
        if (!response) return
        tokenOffsetSettingInfoList.checkLimit(
          {
            params: { needToAddData, force },
            offset: offsetSettingInfo.offset,
          },
          response.tokens.length,
        ) as number
        response.tokens.forEach((token: ITokenInfo) => {
          tokenListByCoinId.value[token.coinId] = arrayPushUnique(
            response.tokens,
            UNIQUEKEY,
            tokenListByCoinId.value[token.coinId],
          )
        })
      }
      return tokenListByCoinId.value
    },
  )

  const getTokenInfo = (tokenId: number, coinId?: number) => {
    if (!tokenId) return
    if (coinId) {
      return tokenListByCoinId.value[coinId].find(
        (token) => token.id === tokenId,
      )
    } else {
      if (!tokenListByCoinId.value) return
      for (const coinId in tokenListByCoinId.value) {
        const finded = tokenListByCoinId.value[coinId].find(
          (token) => token.id === tokenId,
        )
        if (finded) return finded
      }
    }
  }

  const reset = () => {
    coinList.value = []
    filterCoinList.value = []
    tokenListByCoinId.value = {}
  }

  return {
    coinList,
    filterCoinList,
    tokenListByCoinId,
    tokenList,
    fetchCoinList,
    fetchTokenList,
    fetchHeldCoinList,
    searchTokenList,
    getCoinInfo,
    getTokenInfo,
    reset,
  }
})

export { useCoinAPIStore }
